import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';

import { clampKpiRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import type { KpiAxisConfiguration, KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import { DEFAULT_KPI_DATA_RANGE } from 'constants/ppa';
import {
    extractWhitespaceYKpiBoundsFromGridData,
    extractWhitespaceYKpiConfigFromGridData,
    type WhitespaceGridKpiAxisResponse,
} from 'components/pages/WhitespaceAnalysis/whitespaceGridKpiAxis.helpers';

type UseWhitespaceYKpiConfigSyncParams = {
    initialGridResponse?: WhitespaceGridKpiAxisResponse;
    isGridFetching: boolean;
    yAxisKpiControlsMeta: KpiAxisControlsMeta | null;
    yAxisKpiConfig?: KpiAxisConfiguration;
    setYAxisKpiConfig: Dispatch<SetStateAction<KpiAxisConfiguration | undefined>>;
    yAxisKpiDataRangeFromGrid?: KpiDataRange;
    setYAxisKpiDataRangeFromGrid: Dispatch<SetStateAction<KpiDataRange | undefined>>;
    yAxisKpiDataRangeFromMeta: KpiDataRange;
    yAxisKpiDataRange: KpiDataRange;
    skipNextGridFetchRef: MutableRefObject<boolean>;
    /** No-op whenever the selected Y option is an attribute (e.g. Pack_Range) - there is no KPI bin slider to sync in that mode. */
    isYAxisAttribute?: boolean;
};

export const useWhitespaceYKpiConfigSync = ({
    initialGridResponse,
    isGridFetching,
    yAxisKpiControlsMeta,
    yAxisKpiConfig,
    setYAxisKpiConfig,
    yAxisKpiDataRangeFromGrid,
    setYAxisKpiDataRangeFromGrid,
    yAxisKpiDataRangeFromMeta,
    yAxisKpiDataRange,
    skipNextGridFetchRef,
    isYAxisAttribute = false,
}: UseWhitespaceYKpiConfigSyncParams) => {
    useEffect(() => {
        if (isYAxisAttribute || !initialGridResponse || isGridFetching || !yAxisKpiControlsMeta) {
            return;
        }

        const boundsFromApi = extractWhitespaceYKpiBoundsFromGridData(initialGridResponse);
        if (boundsFromApi && !yAxisKpiDataRangeFromGrid) {
            setYAxisKpiDataRangeFromGrid(boundsFromApi);
        }

        const dataRange = boundsFromApi ?? yAxisKpiDataRangeFromMeta ?? { ...DEFAULT_KPI_DATA_RANGE };

        if (!yAxisKpiConfig) {
            const configFromApi = extractWhitespaceYKpiConfigFromGridData(initialGridResponse, dataRange);
            if (configFromApi) {
                const clamped = clampKpiRange(configFromApi.min, configFromApi.max, dataRange);
                // Grid data already reflects API bin_config; syncing state must not refetch.
                skipNextGridFetchRef.current = true;
                setYAxisKpiConfig({ ...configFromApi, ...clamped });
            }
        }
    }, [
        initialGridResponse,
        isGridFetching,
        isYAxisAttribute,
        setYAxisKpiConfig,
        setYAxisKpiDataRangeFromGrid,
        skipNextGridFetchRef,
        yAxisKpiConfig,
        yAxisKpiControlsMeta,
        yAxisKpiDataRangeFromGrid,
        yAxisKpiDataRangeFromMeta,
    ]);

    useEffect(() => {
        if (isYAxisAttribute || !yAxisKpiConfig) return;

        const clamped = clampKpiRange(yAxisKpiConfig.min, yAxisKpiConfig.max, yAxisKpiDataRange);
        if (clamped.min === yAxisKpiConfig.min && clamped.max === yAxisKpiConfig.max) {
            return;
        }

        setYAxisKpiConfig({ ...yAxisKpiConfig, ...clamped });
    }, [isYAxisAttribute, setYAxisKpiConfig, yAxisKpiConfig, yAxisKpiDataRange]);
};
